import { z } from 'zod';
import { UtcDateTimeSchema } from './timestamps.js';
export * from './catalogue.js';
export * from './advice-notes.js';
export * from './customers.js';
export * from './inventory.js';
export * from './migration.js';
export * from './purchasing.js';
export * from './reporting.js';
export * from './sales.js';
export * from './till-sessions.js';
export * from './timestamps.js';
export const HealthStatusSchema = z.enum(['ok', 'unavailable']);
export const HealthResponseSchema = z.object({
    service: z.string().min(1),
    status: HealthStatusSchema,
    timestamp: UtcDateTimeSchema,
});
export const ErrorResponseSchema = z.object({
    error: z.object({
        code: z.string().min(1),
        message: z.string().min(1),
        requestId: z.string().min(1),
    }),
});
export const healthResponseJsonSchema = z.toJSONSchema(HealthResponseSchema);
export const errorResponseJsonSchema = z.toJSONSchema(ErrorResponseSchema);
export const UserRoleSchema = z.enum(['ADMIN', 'MANAGER', 'TILL']);
export const UsernameSchema = z.string().trim().min(3).max(64).regex(/^[A-Za-z0-9._-]+$/);
export const PasswordSchema = z.string().min(12).max(128);
export const OptionalEmailSchema = z.union([z.email().max(320), z.literal(''), z.null()]).transform((value) => value || null);
export const SafeUserSchema = z.object({
    id: z.uuid(),
    username: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    role: UserRoleSchema,
    active: z.boolean(),
    loginEnabled: z.boolean(),
    createdAt: UtcDateTimeSchema,
    updatedAt: UtcDateTimeSchema,
    lastSuccessfulLoginAt: UtcDateTimeSchema.nullable(),
    passwordChangedAt: UtcDateTimeSchema.nullable(),
});
export const LoginRequestSchema = z.object({ username: UsernameSchema, password: z.string().min(1).max(128) });
export const AuthMeResponseSchema = z.object({ user: SafeUserSchema, sessionExpiresAt: UtcDateTimeSchema });
export const CsrfResponseSchema = z.object({ csrfToken: z.string().min(32) });
export const CreateUserRequestSchema = z.object({
    username: UsernameSchema,
    displayName: z.string().trim().min(1).max(160),
    email: OptionalEmailSchema.optional().default(null),
    role: UserRoleSchema,
    active: z.boolean().default(true),
    loginEnabled: z.boolean().default(false),
    password: PasswordSchema.optional(),
});
export const UpdateUserRequestSchema = z.object({
    username: UsernameSchema.optional(),
    displayName: z.string().trim().min(1).max(160).optional(),
    email: OptionalEmailSchema.optional(),
    role: UserRoleSchema.optional(),
    active: z.boolean().optional(),
    loginEnabled: z.boolean().optional(),
}).refine((value) => Object.keys(value).length > 0, { message: 'At least one field is required' });
export const SetPasswordRequestSchema = z.object({ password: PasswordSchema });
export const UserListResponseSchema = z.object({ users: z.array(SafeUserSchema) });
export const RegisteredDeviceSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    locationId: z.uuid(),
    status: z.enum(['PENDING', 'ACTIVE', 'REVOKED']),
    registeredAt: UtcDateTimeSchema.nullable(),
    lastSeenAt: UtcDateTimeSchema.nullable(),
    revokedAt: UtcDateTimeSchema.nullable(),
    createdAt: UtcDateTimeSchema,
});
export const DeviceListResponseSchema = z.object({ devices: z.array(RegisteredDeviceSchema) });
export const LocationSchema = z.object({ id: z.uuid(), code: z.string(), name: z.string() });
export const LocationListResponseSchema = z.object({ locations: z.array(LocationSchema) });
export const CreateEnrollmentRequestSchema = z.object({
    deviceName: z.string().trim().min(1).max(160),
    locationId: z.uuid(),
});
export const EnrollmentCodeResponseSchema = z.object({
    code: z.string(),
    expiresAt: UtcDateTimeSchema,
});
export const ConsumeEnrollmentRequestSchema = z.object({
    code: z.string().trim().min(8).max(32),
    installationId: z.uuid(),
});
export const ConsumeEnrollmentResponseSchema = z.object({
    device: RegisteredDeviceSchema,
    credential: z.string().min(32),
});
